import axios from 'axios';

import { CustomerAccount } from 'root/apps/QuoteMachineIntegration/state/CustomerAccount';
import { V1CreditAccount } from 'root/apps/QuoteMachineIntegration/state/v1/CreditAccount';
import { logError } from 'root/datadog/datadogLogs';

import { LS_INVOICING_API_ROOT } from '../../constants';
import { formatRefId } from '../../utils/referenceHelpers';

/**
 * Maps a v1 /credit-accounts/{idOrRef} response to the internal CustomerAccount model
 * used by the register and customer account page.
 *
 * Field mapping:
 *   balances.outstandingAmount → owingBalance
 *   balances.overdueAmount     → overdueBalance
 *   balances.depositAmount     → depositBalance
 *   balances.availableCredit   → availableAmountFromCreditLimit
 *   creditLimit                → creditLimit
 *   currency                   → currency
 *   id                         → invoicingAccountId
 */
export function adaptV1CreditAccount(response: V1CreditAccount): CustomerAccount {
  return {
    owingBalance: response.balances.outstandingAmount
      ? parseFloat(response.balances.outstandingAmount)
      : null,
    overdueBalance: response.balances.overdueAmount
      ? parseFloat(response.balances.overdueAmount)
      : null,
    depositBalance: response.balances.depositAmount
      ? parseFloat(response.balances.depositAmount)
      : null,
    availableAmountFromCreditLimit: parseFloat(response.balances.availableCredit),
    creditLimit: parseFloat(response.creditLimit),
    currency: response.currency || window.merchantos.locale.currency_code,
    invoicingAccountId: response.id,
    invoicingContactId: response.creditAccountRef.split('_')[1] || '',
    totalProcessing: response.balances.pendingPaymentAmount
      ? parseFloat(response.balances.pendingPaymentAmount)
      : null,
  };
}

/**
 * Fetches a credit account from the Invoicing v1 API and returns it mapped
 * to the internal CustomerAccount model.
 *
 * @param creditAccountId The credit account ID (will be formatted with ls-retail_ prefix)
 * @param creditLimit Optional credit limit to pass as a query param
 */
export async function getCreditAccount(
  creditAccountId: string,
  creditLimit?: string
): Promise<CustomerAccount> {
  const creditAccountRef = formatRefId(creditAccountId);
  const params = creditLimit ? `?credit_limit=${creditLimit}` : '';
  const url = `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}${params}`;

  try {
    const { data } = await axios.get<V1CreditAccount>(url);
    return adaptV1CreditAccount(data);
  } catch (error) {
    logError(error, {
      creditAccountRef,
      message: `Failed to fetch credit account from Invoicing v1 for ref: ${creditAccountRef}`,
    });
    throw error;
  }
}
